import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, MessageSquare, ListChecks, FileText } from "lucide-react";
import { PageHero, SectionHeading } from "../components/shared/Primitives";
import { useAuth } from "../context/AuthContext";

const FEATURES = [
  { icon: MessageSquare, title: "Shared discussion space", body: "An auto-saved conversation thread between you and the family/client representative — never lost in a busy chat app." },
  { icon: ListChecks, title: "Agreement checklist", body: "Guided topics covering work, money, household, care needs, boundaries and safety — so nothing important gets missed." },
  { icon: FileText, title: "Key decisions & draft documents", body: "Record what's actually been agreed, and upload draft agreements so both sides see the same paperwork." },
  { icon: ShieldCheck, title: "Private and time-limited", body: "Only the invited carer and family/client representative can see the room. Expires after 7 days unless activated into the full Care Partnership Room." },
];

export default function CareDiscussionRoomLanding() {
  const { user } = useAuth();
  return (
    <div data-testid="page-care-discussion-room">
      <PageHero
        eyebrow="JCC Care Discussion Room"
        title="A clearer way to start a private care arrangement."
        description="A temporary, private discussion space for a self-employed carer and a family/client representative — to talk through expectations, agree key terms, and decide whether to activate a full Care Partnership Room."
      >
        <div className="mt-8 flex flex-wrap gap-3">
          {user ? (
            <>
              <Link to="/care-discussion-room/new" className="btn-teal" data-testid="cdr-create">
                Create Care Discussion Room <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/care-rooms" className="btn-outline" data-testid="cdr-list">
                View my rooms
              </Link>
            </>
          ) : (
            <Link to="/auth/handover/request?next=%2Fcare-discussion-room%2Fnew" className="btn-teal" data-testid="cdr-login">
              Log in to create a room <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </PageHero>

      <section className="section pt-12 md:pt-16">
        <div className="container-jcc max-w-4xl">
          <div className="card-base p-7 md:p-9">
            <p className="text-ink leading-relaxed">
              WhatsApp and Messenger are useful for quick messages, but they were not built
              for private care arrangements. Your Care Discussion Room is different.
              Communications are automatically recorded and time-stamped, and important
              records are kept in one organised place so they cannot be casually deleted,
              changed, or buried in a busy chat thread. This gives both the carer and the
              family a clearer shared record if questions, concerns or emergencies arise.
            </p>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-jcc">
          <SectionHeading eyebrow="What's inside" title="A simple, private workspace." />
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            {FEATURES.map((f) => (
              <div key={f.title} className="card-base p-6" data-testid={`cdr-feature-${f.title.split(" ")[0].toLowerCase()}`}>
                <div className="w-11 h-11 rounded-xl bg-teal-50 text-teal flex items-center justify-center mb-4">
                  <f.icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-display text-xl font-semibold">{f.title}</h3>
                <p className="mt-2 text-ink-muted text-sm leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section pt-0">
        <div className="container-jcc max-w-3xl">
          <div className="rounded-3xl border border-slate-200 bg-surface p-8">
            <h3 className="font-display text-2xl font-semibold">Important: JCC is not a care agency.</h3>
            <p className="mt-3 text-ink-muted leading-relaxed">
              JCC does not manage, supervise, direct, control or monitor care. The Care
              Discussion Room is a shared workspace — the arrangement itself is between the
              self-employed carer and the family/client representative.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
